import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  Filter, 
  Users,
  UserPlus,
  X,
  Bell,
  Smartphone,
  Activity,
  Mail,
  Phone,
  UserCheck,
  RefreshCw
} from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import { fetchMobileUsers, getMobileUsersCount } from '../../utils/mobileUsersApi';
import '../../styles/UserDashboard.css';

const UserDashboard = () => {
  const navigate = useNavigate();
  
  // State management
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(24);
  const [totalCount, setTotalCount] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showMobileUsers, setShowMobileUsers] = useState(false);
  const [mobileUsers, setMobileUsers] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Fetch users data with proper pagination
  const fetchUsers = async () => {
    if (currentPage === 1) {
      setLoading(true);
    }
    
    try {
      // Calculate range for Supabase query
      const from = (currentPage - 1) * pageSize;
      const to = from + pageSize - 1;

      // SPECIAL CASE: Mobile filter - fetch mobile users directly
      if (roleFilter === 'mobile') {
        // Get all mobile user IDs first
        const { data: mobileData } = await supabase
          .from('user_player_ids')
          .select('user_id');
        
        const mobileUserIds = [...new Set(mobileData?.map(d => d.user_id) || [])];
        
        if (currentPage === 1) {
          setTotalCount(mobileUserIds.length);
        }
        
        if (mobileUserIds.length === 0) {
          setUsers([]);
          setLoading(false);
          return;
        }
        
        // Paginate the mobile user IDs
        const paginatedIds = mobileUserIds.slice(from, to + 1);
        
        // Fetch user details for paginated mobile users
        let query = supabase
          .from('users')
          .select(`
            uuid, 
            email, 
            first_name, 
            last_name, 
            nickname, 
            handle, 
            profile_img,
            preferences,
            mobile_phone,
            created_at, 
            updated_at,
            agreed_to_terms,
            test_account
          `)
          .in('uuid', paginatedIds)
          .order(sortBy, { ascending: sortOrder === 'asc' });

        if (searchQuery) {
          query = query.or(`first_name.ilike.%${searchQuery}%,email.ilike.%${searchQuery}%,handle.ilike.%${searchQuery}%,nickname.ilike.%${searchQuery}%`);
        }

        const { data: mobileUsers, error } = await query;
        
        if (error) throw error;
        
        // Transform and set users
        const usersWithData = await transformUsersWithDevices(mobileUsers || [], mobileUserIds);
        setUsers(usersWithData);
        setLoading(false);
        return;
      }

      // SPECIAL CASE: Active filter - fetch users active in last 7 days
      if (roleFilter === 'active') {
        const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
        
        // Get count of active users
        if (currentPage === 1) {
          const { count: activeCount } = await supabase
            .from('users')
            .select('*', { count: 'exact', head: true })
            .gte('updated_at', oneWeekAgo);
          
          setTotalCount(activeCount || 0);
        }
        
        // Fetch paginated active users
        let query = supabase
          .from('users')
          .select(`
            uuid, 
            email, 
            first_name, 
            last_name, 
            nickname, 
            handle, 
            profile_img,
            preferences,
            mobile_phone,
            created_at, 
            updated_at,
            agreed_to_terms,
            test_account
          `)
          .gte('updated_at', oneWeekAgo)
          .order(sortBy, { ascending: sortOrder === 'asc' })
          .range(from, to);

        if (searchQuery) {
          query = query.or(`first_name.ilike.%${searchQuery}%,email.ilike.%${searchQuery}%,handle.ilike.%${searchQuery}%,nickname.ilike.%${searchQuery}%`);
        }

        const { data: activeUsers, error } = await query;
        
        if (error) throw error;
        
        // Get mobile user IDs for device count
        const { data: mobileData } = await supabase
          .from('user_player_ids')
          .select('user_id');
        const mobileUserIds = mobileData?.map(d => d.user_id) || [];
        
        // Transform and set users
        const usersWithData = await transformUsersWithDevices(activeUsers || [], mobileUserIds);
        setUsers(usersWithData);
        setLoading(false);
        return;
      }

      // NORMAL CASE: Regular filtering
      // First, get the total count (only on first page or when filters change)
      if (currentPage === 1) {
        let countQuery = supabase
          .from('users')
          .select('*', { count: 'exact', head: true });

        if (searchQuery) {
          countQuery = countQuery.or(`first_name.ilike.%${searchQuery}%,last_name.ilike.%${searchQuery}%,email.ilike.%${searchQuery}%,handle.ilike.%${searchQuery}%,nickname.ilike.%${searchQuery}%`);
        }

        const { count: totalCountResult } = await countQuery;
        setTotalCount(totalCountResult || 0);
      }

      // Then fetch the paginated users
      let query = supabase
        .from('users')
        .select(`
          uuid, 
          email, 
          first_name, 
          last_name, 
          nickname, 
          handle, 
          profile_img,
          preferences,
          mobile_phone,
          created_at, 
          updated_at,
          agreed_to_terms,
          test_account
        `)
        .order(sortBy, { ascending: sortOrder === 'asc' })
        .range(from, to);

      if (searchQuery) {
        query = query.or(`first_name.ilike.%${searchQuery}%,email.ilike.%${searchQuery}%,handle.ilike.%${searchQuery}%,nickname.ilike.%${searchQuery}%`);
      }

      const { data: users, error } = await query;
      
      if (error) throw error;
      
      // Apply role filtering in JavaScript (like the original code)
      let filteredUsers = users || [];
      
      if (roleFilter !== 'all') {
        filteredUsers = filteredUsers.filter(user => {
          const userRole = user.preferences?.home;
          
          if (roleFilter === 'unknown') {
            return !userRole || userRole === '' || userRole === 'unknown' || userRole === 'null' || userRole === 'undefined';
          }
          
          // Normalize both the filter and the user role for comparison
          const normalizedFilter = roleFilter.toLowerCase().trim();
          const normalizedUserRole = (userRole || '').toLowerCase().trim();
          
          // Handle common variations
          if (normalizedFilter === 'collector' && (normalizedUserRole === 'collector' || normalizedUserRole === 'collectors')) return true;
          if (normalizedFilter === 'artist' && (normalizedUserRole === 'artist' || normalizedUserRole === 'artists')) return true;
          if (normalizedFilter === 'gallery' && (normalizedUserRole === 'gallery' || normalizedUserRole === 'galleries')) return true;
          if (normalizedFilter === 'viewer' && (normalizedUserRole === 'viewer' || normalizedUserRole === 'viewers')) return true;
          
          return normalizedUserRole === normalizedFilter;
        });
      }
      
      // Get mobile user IDs for device count
      const { data: mobileData } = await supabase
        .from('user_player_ids')
        .select('user_id');
      const mobileUserIds = mobileData?.map(d => d.user_id) || [];
      
      // Transform and set users
      const usersWithData = await transformUsersWithDevices(filteredUsers, mobileUserIds);
      setUsers(usersWithData);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Helper function to transform users with device data
  const transformUsersWithDevices = async (userList, mobileUserIds) => {
    // Transform users with proper data handling and device counts
    const usersWithDevices = userList.map(user => {
      let role = user.preferences?.home || 'unknown';
      
      // Normalize role values
      if (role && typeof role === 'string') {
        role = role.toLowerCase().trim();
        if (role === 'collectors') role = 'collector';
        if (role === 'artists') role = 'artist';
        if (role === 'galleries') role = 'gallery';
        if (role === 'viewers') role = 'viewer';
        if (role === '' || role === 'null' || role === 'undefined') role = 'unknown';
      }

      // Safely handle the last sign in date
      let lastSignIn = null;
      if (user.updated_at && user.updated_at !== 'null' && user.updated_at !== 'undefined') {
        try {
          const date = new Date(user.updated_at);
          if (!isNaN(date.getTime())) {
            lastSignIn = user.updated_at;
          }
        } catch (e) {
          console.warn('Invalid date for user:', user.uuid, user.updated_at);
        }
      }
      
      // Check if user has mobile app
      const hasMobileApp = mobileUserIds.includes(user.uuid);

      return {
        ...user,
        role: role,
        device_count: hasMobileApp ? 1 : 0,
        notification_enabled: user.preferences?.notifications !== false,
        last_sign_in_at: lastSignIn
      };
    });

    // Fetch accurate device counts for all users in this batch
    if (usersWithDevices.length > 0) {
      try {
        const userIds = usersWithDevices.map(user => user.uuid);
        const { data: deviceData, error: deviceError } = await supabase
          .from('user_player_ids')
          .select('user_id')
          .in('user_id', userIds);

        if (!deviceError && deviceData) {
          // Count devices per user
          const deviceCounts = {};
          deviceData.forEach(record => {
            deviceCounts[record.user_id] = (deviceCounts[record.user_id] || 0) + 1;
          });

          // Update users with actual device counts
          usersWithDevices.forEach(user => {
            user.device_count = deviceCounts[user.uuid] || 0;
          });
        }
      } catch (deviceError) {
        console.warn('Error fetching device counts:', deviceError);
      }
    }

    // Fetch artwork counts for artists (only Public artworks)
    if (usersWithDevices.length > 0) {
      try {
        const userIds = usersWithDevices.map(user => user.uuid);
        const { data: artworkData, error: artworkError } = await supabase
          .from('vcs')
          .select('uuid')
          .in('uuid', userIds)
          .eq('access_control', 'Public');

        if (!artworkError && artworkData) {
          // Count artworks per user
          const artworkCounts = {};
          artworkData.forEach(record => {
            artworkCounts[record.uuid] = (artworkCounts[record.uuid] || 0) + 1;
          });

          // Update users with actual artwork counts
          usersWithDevices.forEach(user => {
            user.artwork_count = artworkCounts[user.uuid] || 0;
          });
        }
      } catch (artworkError) {
        console.warn('Error fetching artwork counts:', artworkError);
        usersWithDevices.forEach(user => {
          user.artwork_count = 0;
        });
      }
    }
    
    return usersWithDevices;
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      // Get total users count
      const { count: totalCount } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true });
      
      // Get active users (updated in last 7 days)
      const { count: activeCount } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .gte('updated_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString());
      
      // Get users with notifications enabled
      const { count: notificationCount } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .neq('preferences->notifications', false);
      
      // Get mobile users - actual OneSignal player IDs from user_player_ids table
      let mobileCount = 0;
      try {
        const { count, error } = await getMobileUsersCount();
        
        if (error) {
          console.warn('Could not get mobile users count:', error);
          mobileCount = 0;
        } else {
          mobileCount = count;
        }
        
        // Set mobile users to empty array for now (will be populated when card is clicked)
        setMobileUsers([]);
      } catch (error) {
        console.warn('Exception getting mobile users count:', error);
        mobileCount = 0;
        setMobileUsers([]);
      }

      setStats({
        total: totalCount || 0,
        activeThisWeek: activeCount || 0,
        notificationsEnabled: notificationCount || 0,
        mobileAppUsers: mobileCount || 0
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchStats();
  }, [searchQuery, roleFilter, sortBy, sortOrder, currentPage, pageSize]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page when searching
    fetchUsers();
  };

  const clearSearch = () => {
    setSearchQuery('');
    setCurrentPage(1);
    // The useEffect will trigger fetchUsers when searchQuery changes
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page when changing size
  };

  const handleUserClick = (userId) => {
    // Navigate to user detail page
    navigate(`/admin/users/${userId}`);
  };

  const handleMobileUsersClick = () => {
    // Toggle mobile filter - if already filtering by mobile, show all
    if (roleFilter === 'mobile') {
      setRoleFilter('all');
    } else {
      setRoleFilter('mobile');
    }
    setCurrentPage(1);
  };

  const handleActiveUsersClick = () => {
    // Toggle active filter - if already filtering by active, show all
    if (roleFilter === 'active') {
      setRoleFilter('all');
    } else {
      setRoleFilter('active');
    }
    setCurrentPage(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  const getRoleBadgeColor = (role) => {
    switch (role?.toLowerCase()) {
      case 'artist': return 'artist';
      case 'collector': return 'collector';
      case 'admin': return 'admin';
      case 'curator': return 'curator';
      default: return 'default';
    }
  };

  return (
    <div className="user-dashboard">
      {/* Header */}
      <div className="user-header">
        <div className="user-header-content">
          <div>
            <h1 className="user-title">User Management</h1>
            <p className="user-subtitle">Monitor app usage and send system notifications</p>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button 
              className="add-user-btn"
              onClick={() => navigate('/admin/notifications/send')}
              style={{ backgroundColor: '#6f42c1' }}
              title="Send push notifications to mobile users"
            >
              <Bell className="w-4 h-4" />
              Send Notifications
            </button>
            <button 
              className="add-user-btn"
              onClick={() => navigate('/admin/users/create')}
            >
              <UserPlus className="w-4 h-4" />
              Add New User
            </button>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="user-stats-grid">
        <div className="user-stat-card">
                      <div className="stat-icon">
              <Users size={20} color="#007bff" />
            </div>
          <div className="stat-content">
            <div className="stat-number">{stats?.total || 0}</div>
            <div className="stat-label">TOTAL USERS</div>
          </div>
        </div>
        
        <div className={`user-stat-card clickable ${roleFilter === 'active' ? 'active' : ''}`} onClick={handleActiveUsersClick}>
                      <div className="stat-icon">
              <Activity size={20} color="#28a745" />
            </div>
          <div className="stat-content">
            <div className="stat-number">{stats?.activeThisWeek || 0}</div>
            <div className="stat-label">ACTIVE THIS WEEK</div>
            {roleFilter === 'active' && <div className="filter-hint">Click to show all</div>}
          </div>
        </div>
        
        <div className={`user-stat-card clickable ${roleFilter === 'mobile' ? 'active' : ''}`} onClick={handleMobileUsersClick}>
                      <div className="stat-icon">
              <Smartphone size={20} color="#6f42c1" />
            </div>
          <div className="stat-content">
            <div className="stat-number">{stats?.mobileAppUsers || 0}</div>
            <div className="stat-label">MOBILE APP USERS</div>
            {roleFilter === 'mobile' && <div className="filter-hint">Click to show all</div>}
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="user-controls">
        <div className="user-search-container">
          <div className="search-input-wrapper">
            <Search size={16} className="search-icon" color="#6c757d" />
            <input
              type="text"
              placeholder="Search users by name, email, or handle..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {searchQuery && (
              <button 
                type="button" 
                onClick={clearSearch}
                className="clear-search-btn"
                aria-label="Clear search"
              >
                <X size={16} color="#6c757d" />
              </button>
            )}
          </div>
        </div>

        {/* Filter Controls */}
        <div className="filter-controls">
          <label htmlFor="role-filter">Filter by Role:</label>
          <select
            id="role-filter"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Roles</option>
            <option value="artist">Artist</option>
            <option value="collector">Collector</option>
            <option value="mobile">📱 Mobile App Users</option>
          </select>
        </div>

        {/* Sort Controls */}
        <div className="sort-controls">
          <label htmlFor="sort-by">Sort by:</label>
          <select
            id="sort-by"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="sort-select"
          >
            <option value="created_at">Join Date</option>
            <option value="last_sign_in_at">Last Active</option>
            <option value="first_name">First Name</option>
            <option value="last_name">Last Name</option>
            <option value="email">Email</option>
          </select>
          
          <button
            className={`sort-direction-btn ${sortOrder === 'asc' ? 'asc' : 'desc'}`}
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
          >
            {sortOrder === 'asc' ? '↑' : '↓'}
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading users...</p>
        </div>
      )}

      {/* Users Container */}
      {!loading && (
        <div className="users-container list">
          {/* Filter indicator */}
          {roleFilter === 'mobile' && (
            <div className="filter-active-bar">
              <Smartphone size={16} />
              <span>Showing only mobile app users</span>
              <button onClick={() => setRoleFilter('all')} className="clear-filter-btn">
                <X size={14} /> Clear
              </button>
            </div>
          )}
          {roleFilter === 'active' && (
            <div className="filter-active-bar" style={{ background: 'linear-gradient(90deg, #28a745 0%, #20c997 100%)' }}>
              <Activity size={16} />
              <span>Showing users active in the last 7 days</span>
              <button onClick={() => setRoleFilter('all')} className="clear-filter-btn">
                <X size={14} /> Clear
              </button>
            </div>
          )}
          
          {/* User List */}
          <div className="user-table compact">
              {users.map((user) => (
                <div key={user.uuid} className={`user-row compact ${user.device_count > 0 ? 'has-app' : ''}`} onClick={() => handleUserClick(user.uuid)}>
                  {/* Avatar */}
                  <div className="cell-avatar">
                    {user.profile_img ? (
                      <img 
                        src={user.profile_img} 
                        alt=""
                        className="avatar-img"
                      />
                    ) : (
                      <div className="avatar-initials">
                        {user.first_name?.[0] || user.email?.[0] || '?'}
                      </div>
                    )}
                  </div>
                  
                  {/* Name block */}
                  <div className="cell-name-block">
                    <span className="name-line">
                      {user.first_name || ''} {user.last_name || ''}
                    </span>
                    {user.nickname && <span className="nickname-line">{user.nickname}</span>}
                    {user.handle && <span className="handle-line">@{user.handle}</span>}
                  </div>
                  
                  {/* Contact block */}
                  <div className="cell-contact">
                    <span className="email-line">{user.email}</span>
                    {user.mobile_phone && <span className="phone-line">{user.mobile_phone}</span>}
                  </div>
                  
                  {/* Dates block */}
                  <div className="cell-dates">
                    <span className="date-item">Active: {formatDate(user.last_sign_in_at)}</span>
                    <span className="date-item">Joined: {formatDate(user.created_at)}</span>
                  </div>
                  
                  {/* Mobile app indicator */}
                  <div className="cell-app-status">
                    {user.device_count > 0 ? (
                      <span className="has-app-badge">
                        <Smartphone size={14} />
                        App
                      </span>
                    ) : (
                      <span className="no-app-badge">No App</span>
                    )}
                  </div>
                  
                  {/* Role badge */}
                  <span className={`role-tag ${getRoleBadgeColor(user.role)}`}>
                    {user.role || '?'}
                    {user.role === 'artist' && user.artwork_count > 0 && (
                      <span className="artwork-count"> ({user.artwork_count})</span>
                    )}
                  </span>
                </div>
              ))}
            </div>
        </div>
      )}

      {/* Pagination */}
      {!loading && totalCount > 0 && (
        <div className="pagination">
          <div className="pagination-info">
            <p>Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} users</p>
          </div>
          
          <div className="pagination-controls">
            <button
              className="page-btn"
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              Previous
            </button>
            
            <div className="page-numbers">
              {/* Show first page */}
              {currentPage > 3 && (
                <>
                  <button
                    className="page-btn"
                    onClick={() => handlePageChange(1)}
                  >
                    1
                  </button>
                  {currentPage > 4 && <span className="page-ellipsis">...</span>}
                </>
              )}
              
              {/* Show pages around current page */}
              {Array.from({ length: Math.min(5, Math.ceil(totalCount / pageSize)) }, (_, i) => {
                const pageNum = Math.max(1, currentPage - 2) + i;
                const totalPages = Math.ceil(totalCount / pageSize);
                if (pageNum > totalPages) return null;
                return (
                  <button
                    key={pageNum}
                    className={`page-btn ${pageNum === currentPage ? 'active' : ''}`}
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
              
              {/* Show last page */}
              {currentPage < Math.ceil(totalCount / pageSize) - 2 && (
                <>
                  {currentPage < Math.ceil(totalCount / pageSize) - 3 && <span className="page-ellipsis">...</span>}
                  <button
                    className="page-btn"
                    onClick={() => handlePageChange(Math.ceil(totalCount / pageSize))}
                  >
                    {Math.ceil(totalCount / pageSize)}
                  </button>
                </>
              )}
            </div>
            
            <button
              className="page-btn"
              disabled={currentPage === Math.ceil(totalCount / pageSize)}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              Next
            </button>
          </div>
          
          <div className="pagination-size">
            <label htmlFor="page-size">Items per page:</label>
            <select
              id="page-size"
              value={pageSize}
              onChange={(e) => {
                const newSize = parseInt(e.target.value);
                handlePageSizeChange(newSize);
              }}
              className="page-size-select"
            >
              <option value={12}>12</option>
              <option value={24}>24</option>
              <option value={48}>48</option>
              <option value={96}>96</option>
            </select>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && users.length === 0 && (
        <div className="empty-state">
          <Users size={48} className="empty-icon" />
          <h3>No users found</h3>
          <p>Try adjusting your search or filters to find what you're looking for.</p>
        </div>
      )}
        </div>
    );
  };

export default UserDashboard;